import React, { useState, useRef } from 'react';
import { X, Mail, AlertCircle } from 'lucide-react';

interface EmailChangeModalProps {
  currentEmail: string;
  newEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'verify-current' | 'verify-new' | 'success';

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
}

const CodeInput: React.FC<CodeInputProps> = ({ value, onChange, onComplete }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(6, ' ').split('').slice(0, 6);

  const handleChange = (index: number, inputValue: string) => {
    // Only allow digits
    const digit = inputValue.replace(/\D/g, '');
    
    if (digit.length > 1) {
      // Handle paste
      const pastedDigits = digit.slice(0, 6);
      onChange(pastedDigits);
      
      // Focus on the last filled input or next empty
      const nextIndex = Math.min(pastedDigits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      
      if (pastedDigits.length === 6 && onComplete) {
        onComplete(pastedDigits);
      }
      return;
    }

    // Update the digit at this position
    const newDigits = [...digits];
    newDigits[index] = digit;
    const newValue = newDigits.join('').replace(/ /g, '');
    onChange(newValue);

    // Move to next input if digit entered
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete if all 6 digits are filled
    if (newValue.length === 6 && onComplete) {
      onComplete(newValue);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index].trim() && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      onChange(pastedData);
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
      
      if (pastedData.length === 6 && onComplete) {
        onComplete(pastedData);
      }
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit.trim()}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      ))}
    </div>
  );
};

export const EmailChangeModal: React.FC<EmailChangeModalProps> = ({
  currentEmail,
  newEmail,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<Step>('verify-current');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleVerifyCurrentEmail = async (codeValue?: string) => {
    const verifyCode = codeValue || code;
    if (!verifyCode.trim()) {
      setError('Please enter verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/verify-current-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: verifyCode })
      });

      const data = await response.json();

      if (data.success) {
        setCode('');
        setStep('verify-new');
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError('Failed to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyNewEmail = async (codeValue?: string) => {
    const verifyCode = codeValue || code;
    if (!verifyCode.trim()) {
      setError('Please enter verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/verify-new-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: verifyCode })
      });

      const data = await response.json();

      if (data.success) {
        setStep('success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError('Failed to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeComplete = (completeCode: string) => {
    if (step === 'verify-current') {
      handleVerifyCurrentEmail(completeCode);
    } else if (step === 'verify-new') {
      handleVerifyNewEmail(completeCode);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {step === 'success' ? 'Success!' : 'Verify Email Change'}
          </h3>
          {step !== 'success' && (
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'verify-current' && (
            <div className="space-y-6">
              <div className="flex items-start space-x-3 p-3 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 rounded-lg">
                <Mail className="w-5 h-5 text-accent-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-accent-900 dark:text-accent-100 font-medium">
                    Verification code sent
                  </p>
                  <p className="text-sm text-accent-700 dark:text-accent-300 mt-1">
                    Check your current email (<strong>{currentEmail}</strong>) for a 6-digit code.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                  Enter verification code
                </label>
                <CodeInput
                  value={code}
                  onChange={setCode}
                  onComplete={handleCodeComplete}
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 p-3 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-error-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
                </div>
              )}
            </div>
          )}

          {step === 'verify-new' && (
            <div className="space-y-6">
              <div className="flex items-start space-x-3 p-3 bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 rounded-lg">
                <Mail className="w-5 h-5 text-success-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-success-900 dark:text-success-100 font-medium">
                    Current email verified ✓
                  </p>
                  <p className="text-sm text-success-700 dark:text-success-300 mt-1">
                    Check your new email (<strong>{newEmail}</strong>) for a 6-digit code.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
                  Enter verification code
                </label>
                <CodeInput
                  value={code}
                  onChange={setCode}
                  onComplete={handleCodeComplete}
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 p-3 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-error-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
                </div>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Email Changed Successfully
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                Your email has been updated to <strong>{newEmail}</strong>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

